/**
 * AudioPort implementation: Web Audio API procedural SFX.
 * Theme-aware: holy/bright tones for player feedback, low/dissonant for monsters.
 * No external audio files; every sound is synthesized at call time.
 * Errors (jsdom, blocked autoplay) are swallowed so tests and non-audio
 * environments stay green.
 */

import type { AudioPort } from "../../application/ports.js";

type Voice = {
  freq: number;
  type?: OscillatorType;
  delay?: number;
  duration: number;
  volume: number;
  /** Optional biquad filter applied to this voice. */
  filter?: { type: BiquadFilterType; freq: number; q?: number };
  /** Optional portamento: target frequency reached over duration. */
  slideTo?: number;
};

export function createWebAudioAdapter(): AudioPort & {
  setMuted(muted: boolean): void;
  isMuted(): boolean;
} {
  let ctx: AudioContext | null = null;
  let muted = false;
  if (typeof localStorage !== "undefined" && localStorage.getItem("gc_audio_muted") === "1") {
    muted = true;
  }

  function getCtx(): AudioContext {
    if (!ctx) {
      ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    }
    return ctx;
  }

  function playVoice(v: Voice): void {
    try {
      const c = getCtx();
      const t0 = c.currentTime + (v.delay ?? 0);
      const osc = c.createOscillator();
      osc.type = v.type ?? "sine";
      osc.frequency.setValueAtTime(v.freq, t0);
      if (v.slideTo !== undefined) {
        osc.frequency.exponentialRampToValueAtTime(Math.max(10, v.slideTo), t0 + v.duration);
      }

      const gain = c.createGain();
      // ADSR-lite: quick attack, exponential release.
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(v.volume, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + v.duration);

      const chainEnd: AudioNode = gain;
      if (v.filter) {
        const bq = c.createBiquadFilter();
        bq.type = v.filter.type;
        bq.frequency.value = v.filter.freq;
        bq.Q.value = v.filter.q ?? 1;
        osc.connect(bq);
        bq.connect(gain);
      } else {
        osc.connect(gain);
      }
      chainEnd.connect(c.destination);

      osc.start(t0);
      osc.stop(t0 + v.duration + 0.05);
    } catch {
      /* ignore audio errors */
    }
  }

  function playChord(voices: Voice[]): void {
    for (const v of voices) playVoice(v);
  }

  function playNoise(duration: number, volume: number, filterFreq: number): void {
    try {
      const c = getCtx();
      const bufSize = Math.max(1, Math.floor(c.sampleRate * duration));
      const buf = c.createBuffer(1, bufSize, c.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) {
        // Pink-ish noise (average of two whites for mellow decay).
        data[i] = (Math.random() * 2 - 1) * (1 - i / bufSize);
      }
      const src = c.createBufferSource();
      src.buffer = buf;

      const filter = c.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = filterFreq;
      filter.Q.value = 0.7;

      const gain = c.createGain();
      gain.gain.setValueAtTime(volume, c.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + duration);

      src.connect(filter);
      filter.connect(gain);
      gain.connect(c.destination);
      src.start();
      src.stop(c.currentTime + duration + 0.05);
    } catch {
      /* ignore */
    }
  }

  return {
    setMuted(v: boolean) {
      muted = v;
      if (typeof localStorage !== "undefined") {
        if (v) localStorage.setItem("gc_audio_muted", "1");
        else localStorage.removeItem("gc_audio_muted");
      }
    },
    isMuted() { return muted; },
    play(soundId: string) {
      if (muted) return;
      switch (soundId) {
        case "build":
          // Consecrating chapel: soft two-note bell chime (C5 + E5).
          playChord([
            { freq: 523.25, type: "sine", duration: 0.55, volume: 0.22, filter: { type: "lowpass", freq: 2400 } },
            { freq: 659.25, type: "sine", duration: 0.7, volume: 0.16, delay: 0.08, filter: { type: "lowpass", freq: 2600 } },
          ]);
          break;
        case "click":
          // Bright paper flick.
          playVoice({ freq: 880, type: "triangle", duration: 0.06, volume: 0.1 });
          break;
        case "shot":
          // Quick bright chirp; low volume so rapid fire doesn't deafen.
          playVoice({ freq: 1800, type: "triangle", duration: 0.05, volume: 0.06, slideTo: 1200, filter: { type: "highpass", freq: 600 } });
          break;
        case "hit":
          // Metallic tink (short square + noise).
          playVoice({ freq: 420, type: "square", duration: 0.08, volume: 0.1 });
          playNoise(0.04, 0.05, 3200);
          break;
        case "monsterDeath":
          // Evil hiss + low falling groan.
          playNoise(0.18, 0.12, 1400);
          playVoice({ freq: 220, type: "sawtooth", duration: 0.25, volume: 0.12, slideTo: 90, filter: { type: "lowpass", freq: 800 } });
          break;
        case "orbHit":
          // Altar struck: deep impact + warning tremolo.
          playVoice({ freq: 80, type: "sawtooth", duration: 0.45, volume: 0.28, slideTo: 50, filter: { type: "lowpass", freq: 500 } });
          playVoice({ freq: 220, type: "square", duration: 0.3, volume: 0.12, delay: 0.05, filter: { type: "lowpass", freq: 900 } });
          break;
        case "waveStart":
          // Horde approaches: a two-note warning (fifth drop).
          playChord([
            { freq: 392, type: "triangle", duration: 0.25, volume: 0.18 },
            { freq: 294, type: "triangle", duration: 0.3, volume: 0.18, delay: 0.18 },
          ]);
          break;
        case "win": {
          // Triumphant rising fanfare (C major triad + octave).
          playChord([
            { freq: 523.25, type: "triangle", duration: 0.4, volume: 0.24 },
            { freq: 659.25, type: "triangle", duration: 0.4, volume: 0.24, delay: 0.18 },
            { freq: 784.0, type: "triangle", duration: 0.5, volume: 0.24, delay: 0.36 },
            { freq: 1046.5, type: "sine", duration: 0.7, volume: 0.16, delay: 0.54 },
          ]);
          break;
        }
        case "lose":
          // Sorrowful descent (minor triad falling).
          playChord([
            { freq: 349.23, type: "sawtooth", duration: 0.4, volume: 0.22, filter: { type: "lowpass", freq: 1400 } },
            { freq: 277.18, type: "sawtooth", duration: 0.45, volume: 0.22, delay: 0.25, filter: { type: "lowpass", freq: 1300 } },
            { freq: 220.0, type: "sawtooth", duration: 0.6, volume: 0.22, delay: 0.5, filter: { type: "lowpass", freq: 1200 } },
          ]);
          break;
        case "start":
          // Page turn / journey beginning — soft rising ping.
          playChord([
            { freq: 440, type: "sine", duration: 0.18, volume: 0.18 },
            { freq: 554.37, type: "sine", duration: 0.22, volume: 0.2, delay: 0.1 },
          ]);
          break;
        case "earlyWaveMana":
          // AS3 SndWaveBricksSpeedUp analogue: quick bright reward ping.
          playVoice({ freq: 990, type: "sine", duration: 0.12, volume: 0.2, slideTo: 1320, filter: { type: "highpass", freq: 500 } });
          break;
      }
    },
  };
}
